import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject, switchMap, takeUntil, filter } from 'rxjs'
import { Chart, registerables } from 'chart.js'
import { ConcursoResumoDTO } from 'src/app/models/concurso-resumo'
import { ConcursoService } from 'src/app/services/concurso.service'
import { ConcursoDetalheStore } from '../concurso-detalhe/concurso-detalhe.store'

Chart.register(...registerables)

interface StepItem {
  status: 'done' | 'action' | 'pending'
  text: string
}

interface Step {
  label: string
  state: 'completed' | 'active' | 'pending'
  title: string
  items: StepItem[]
}

@Component({
  selector: 'app-concurso-resumo',
  templateUrl: './concurso-resumo.component.html',
  styleUrls: ['./concurso-resumo.component.scss'],
})
export class ConcursoResumoComponent implements OnInit, OnDestroy {
  resumo: ConcursoResumoDTO | null = null
  loading = true
  steps: Step[] = []
  selectedStep = 0

  @ViewChild('chartInscricoes') chartInscricoesRef!: ElementRef<HTMLCanvasElement>
  @ViewChild('chartRecursosStatus') chartRecursosStatusRef!: ElementRef<HTMLCanvasElement>
  @ViewChild('chartRecursosTipo') chartRecursosTipoRef!: ElementRef<HTMLCanvasElement>
  @ViewChild('chartComissao') chartComissaoRef!: ElementRef<HTMLCanvasElement>
  @ViewChild('chartSigad') chartSigadRef!: ElementRef<HTMLCanvasElement>
  @ViewChild('chartClassificacao') chartClassificacaoRef!: ElementRef<HTMLCanvasElement>
  @ViewChild('chartDigitalizacao') chartDigitalizacaoRef!: ElementRef<HTMLCanvasElement>

  private destroy$ = new Subject<void>()
  private charts: Chart[] = []

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private concursoService: ConcursoService,
    private store: ConcursoDetalheStore,
  ) {}

  ngOnInit(): void {
    const params$ = this.route.parent?.params || this.route.params
    params$.pipe(
      filter(p => !!p['id']),
      switchMap(p => {
        this.loading = true
        this.charts.forEach(c => c.destroy())
        this.charts = []
        return this.concursoService.getResumo(+p['id'])
      }),
      takeUntil(this.destroy$)
    ).subscribe(data => {
      this.resumo = data
      this.buildSteps()
      this.selectedStep = this.steps.findIndex(s => s.state === 'active')
      if (this.selectedStep < 0) this.selectedStep = this.steps.findIndex(s => s.state === 'pending')
      if (this.selectedStep < 0) this.selectedStep = this.steps.length - 1
      this.loading = false
      setTimeout(() => this.createCharts(), 0)
    })
  }


  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.charts.forEach(c => c.destroy())
  }

  showDetail(index: number): void {
    this.selectedStep = index
  }


  /**
   * Salva contexto no localStorage antes de navegar para Recursos.
   * A navegação é feita pelo routerLink no template.
   */
  salvarContextoRecursos(): void {
    localStorage.setItem('sinsc_lista-recurso.fromResumo', JSON.stringify(this.store.concurso?.titulo || ''))
  }

  onStepKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.showDetail(Math.min(index + 1, this.steps.length - 1))
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.showDetail(Math.max(index - 1, 0))
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.showDetail(index)
    }
  }

  get inscricoesLabels(): string[] {
    return Object.keys(this.resumo?.inscricoesPorStatus || {})
  }

  formatStatus(status: string): string {
    const map: Record<string, string> = {
      DEFERIDA: 'Deferida',
      DEFERIDA_SEM_PCD: 'Deferida (ampla)',
      INDEFERIDA: 'Indeferida',
      NAO_CONFIRMADA: 'Não confirmada',
      HETEROIDENTIFICACAO: 'Heteroidentificação',
    }
    return map[status] || status.replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase())
  }


  get sigadEnviados(): number {
    return this.resumo?.enviosSigad.filter(e => e.enviados >= e.total && e.total > 0).length || 0
  }


  getPercent(value: number, total: number): number {
    return total > 0 ? Math.round((value / total) * 100) : 0
  }

  formatTipoRecurso(tipo: string): string {
    const t = (tipo || '').toLowerCase()
    if (t.includes('inscri')) return 'Análise de Inscrições'
    if (t.includes('fase ii') || t.includes('fase 2') || t.includes('fase2') || t.includes('11.2.5.2')) return 'Resultado Fase 2'
    if (t.includes('fase i') || t.includes('fase 1') || t.includes('fase1') || t.includes('11.2.5.1')) return 'Resultado Fase 1'
    if (t.includes('comiss') || t.includes('impugna') || t.includes('banca')) return 'Comissão Julgadora'
    if (t.includes('gabarito')) return 'Gabarito'
    if (t.includes('hetero') || t.includes('ppi')) return 'Heteroidentificação'
    return tipo.length > 25 ? tipo.substring(0, 25) + '…' : tipo
  }



  get comissaoTitulares(): number {
    return this.resumo?.membrosComissao.filter(m => m.papel === 'TITULAR').length || 0
  }

  get comissaoSuplentes(): number {
    return this.resumo?.membrosComissao.filter(m => m.papel === 'SUPLENTE').length || 0
  }

  formatFase(fase: string): string {
    if (!fase) return ''
    return fase.replace(/FASE_?/gi, 'Fase ').trim()
  }

  get comissaoPresidente(): number {
    return this.resumo?.membrosComissao.filter(m => m.papel === 'PRESIDENTE').length || 0
  }

  private buildSteps(): void {
    if (!this.resumo) return
    const c = this.resumo.andamentosConcluidos
    const has = (t: string) => c.includes(t)
    const deferidas = (this.resumo.inscricoesPorStatus['DEFERIDA'] || 0) + (this.resumo.inscricoesPorStatus['DEFERIDA_SEM_PCD'] || 0)
    const indeferidas = this.resumo.inscricoesPorStatus['INDEFERIDA'] || 0
    const naoConfirmadas = this.resumo.inscricoesPorStatus['NAO_CONFIRMADA'] || 0
    const provasF1 = this.resumo.provas.filter(p => p.fase === 'FASE_1' || p.fase === 'FASE1')
    const provasF2 = this.resumo.provas.filter(p => p.fase === 'FASE_2' || p.fase === 'FASE2')

    const stepDefs: { label: string; title: string; check: () => boolean; items: () => StepItem[] }[] = [
      {
        label: 'Abertura', title: '1. Abertura e Inscrições',
        check: () => this.resumo!.totalInscricoes > 0,
        items: () => [
          { status: 'done', text: 'Criar concurso' },
          { status: has('ABERTURA') ? 'done' : 'pending', text: 'Publicar andamento: ABERTURA' },
          { status: this.resumo!.totalInscricoes > 0 ? 'done' : 'pending', text: `Receber inscrições (${this.resumo!.totalInscricoes})` }
        ]
      },
      {
        label: 'Análise', title: '2. Análise das Inscrições',
        check: () => has('DEFERIMENTO_INSCRICOES'),
        items: () => [
          { status: (this.resumo!.totalInscricoes > 0 && naoConfirmadas === 0) ? 'done' : this.resumo!.totalInscricoes > 0 ? 'action' : 'pending', text: 'Analisar inscrições' },
          { status: has('DEFERIMENTO_INSCRICOES') ? 'done' : 'pending', text: 'Publicar andamento: ANÁLISE DAS INSCRIÇÕES' },
          { status: !has('DEFERIMENTO_INSCRICOES') ? 'pending' : this.resumo!.recursosInscricaoPendentes === 0 ? 'done' : 'action', text: 'Decidir situação de recursos abertos e alterar inscrições' },
          ...(this.resumo!.tipo === 'PTPP' ? [{ status: (this.resumo!.totalInscricoes > 0 && naoConfirmadas === 0) ? (this.resumo!.heteroidentificacaoConcluida ? 'done' : 'action') : 'pending', text: 'Concluir processo de heteroidentificação' } as StepItem] : []),
        ]
      },
      {
        label: 'Comissão', title: '3. Comissão Julgadora',
        check: () => has('LISTA_DEFINITIVA_INSCRITOS') || has('IMPUGNACAO_COMISSAO'),
        items: () => [
          { status: (this.comissaoTitulares + this.comissaoPresidente) >= 5 ? 'done' : this.resumo!.comissaoTotalAtivos > 0 ? 'action' : 'pending', text: 'Compor a comissão julgadora' },
          { status: has('LISTA_DEFINITIVA_INSCRITOS') || has('IMPUGNACAO_COMISSAO') ? 'done' : 'pending', text: 'Publicar andamento: IMPUGNAÇÃO ou LISTA DEFINITIVA' },
          { status: !(has('IMPUGNACAO_COMISSAO') || has('LISTA_DEFINITIVA_INSCRITOS')) ? 'pending' : this.resumo!.recursosComissaoPendentes === 0 ? 'done' : 'action', text: 'Decidir situação de recursos abertos e alterar comissão' }
        ]
      },
      {
        label: 'Fase 1', title: '4. Fase 1 — Provas e Notas',
        check: () => has('RESULTADO_FASE_1'),
        items: () => [
          { status: (() => {
            if (provasF1.length === 0) return 'pending' as const
            const temData = provasF1[0].temDataCorrecao
            const temEscrita = provasF1.some(p => p.nome === 'ESCRITA')
            const digitOk = !temEscrita || this.resumo!.provasDigitalizadas >= this.resumo!.provasDigitalizadasTotal
            return (temData && digitOk) ? 'done' : 'action'
          })(), text: 'Configurar provas e datas' },
          { status: provasF1.length === 0 ? 'pending' : provasF1.filter(p => p.totalPresenca > 0).length > 0 && provasF1.filter(p => p.totalPresenca > 0).every(p => p.presencaCompleta) ? 'done' : provasF1.some(p => p.presencaRegistrada > 0) ? 'action' : 'pending', text: 'Registrar lista de presença' },
          { status: has('RESULTADO_FASE_1') ? 'done' : this.resumo!.fase1Consolidada ? 'action' : 'pending', text: 'Publicar andamento: RESULTADO FASE 1' }
        ]
      },
      {
        label: 'Fase 2', title: '5. Fase 2 — Provas e Notas',
        check: () => has('RESULTADO_FINAL'),
        items: () => [
          { status: (() => {
            if (provasF2.length === 0 || !provasF2[0].temDataCorrecao) return 'pending' as const
            const temEscritaF2 = provasF2.some(p => p.nome === 'ESCRITA')
            const digitOkF2 = !temEscritaF2 || this.resumo!.provasDigitalizadas >= this.resumo!.provasDigitalizadasTotal
            if (!digitOkF2) return 'action' as const
            return (provasF1.length + provasF2.length) >= 5 ? 'done' : 'action'
          })(), text: 'Configurar provas e datas' },
          { status: this.resumo!.comunicadoTitulosPublicado ? 'done' : 'pending', text: 'Publicar andamento: COMUNICADO (Divulgação Notas da Prova de Títulos)' },
          { status: provasF2.filter(p => p.totalPresenca > 0).length > 0 && provasF2.filter(p => p.totalPresenca > 0).every(p => p.presencaCompleta) ? 'done' : provasF2.some(p => p.presencaRegistrada > 0) ? 'action' : 'pending', text: 'Registrar lista de presença' },
          { status: has('RESULTADO_FINAL') ? 'done' : this.resumo!.fase2Consolidada ? 'action' : 'pending', text: 'Publicar andamento: RESULTADO FINAL PROVISÓRIO' }
        ]
      },
      {
        label: 'Resultado', title: '6. Resultado Final',
        check: () => has('RESULTADO_FINAL_DEFINITIVO'),
        items: () => [
          { status: has('RESULTADO_FINAL_DEFINITIVO') ? 'done' : 'pending', text: 'Publicar andamento: RESULTADO FINAL DEFINITIVO' },
          // { status: has('RESULTADO_FINAL_DEFINITIVO') ? 'done' : 'pending', text: 'Aguardar prazo de recurso' },
        ]
      },
      {
        label: 'SIGAD', title: '7. Processo Digital SIGAD',
        check: () => this.resumo!.enviosSigad.every(e => e.enviados >= e.total && e.total > 0),
        items: () => [
          { status: this.resumo!.enviosSigad.every(e => e.enviados >= e.total && e.total > 0) ? 'done' : 'pending', text: 'Enviar documentação para SIGAD' },

        ]
      },
      {
        label: 'Homologação', title: '8. Homologação',
        check: () => has('HOMOLOGACAO'),
        items: () => [
          { status: has('HOMOLOGACAO') ? 'done' : 'pending', text: 'Publicar andamento: HOMOLOGAÇÃO' },
        ]
      },
    ]
    this.steps = stepDefs.map(def => {
      const items = def.items()
      const completed = def.check()
      const hasProgress = items.some(i => i.status === 'done' || i.status === 'action')
      let state: 'completed' | 'active' | 'pending'
      if (completed) {
        state = 'completed'
      } else if (hasProgress) {
        state = 'active'
      } else {
        state = 'pending'
      }
      return { label: def.label, state, title: def.title, items }
    })
  }


  private createCharts(): void {
    if (!this.resumo) return

    // Inscrições doughnut
    if (this.chartInscricoesRef?.nativeElement) {
      const labels = Object.keys(this.resumo.inscricoesPorStatus)
      const data = Object.values(this.resumo.inscricoesPorStatus)
      const colors = labels.map(l => this.getStatusColor(l))
      this.charts.push(new Chart(this.chartInscricoesRef.nativeElement, {
        type: 'doughnut',
        data: { labels: labels.map(l => this.formatStatus(l)), datasets: [{ data, backgroundColor: colors }] },
        options: { responsive: true, maintainAspectRatio: true, plugins: { legend: { display: false } } }
      }))
    }

    // Recursos por status
    if (this.chartRecursosStatusRef?.nativeElement && this.resumo.totalRecursos > 0) {
      const labels = Object.keys(this.resumo.recursosPorStatus)
      const data = Object.values(this.resumo.recursosPorStatus)
      const colors = labels.map(l => this.getParecerColor(l))
      this.charts.push(new Chart(this.chartRecursosStatusRef.nativeElement, {
        type: 'doughnut',
        data: { labels, datasets: [{ data, backgroundColor: colors }] },
        options: {
          responsive: true, maintainAspectRatio: true,
          plugins: { legend: { position: 'bottom', labels: { font: { size: 10 } } }, title: { display: true, text: 'Por Status', font: { size: 11 } } }
        }
      }))
    }

    // Recursos por tipo
    if (this.chartRecursosTipoRef?.nativeElement && this.resumo.totalRecursos > 0) {
      const labels = Object.keys(this.resumo.recursosPorTipo).map(k => this.formatTipoRecurso(k))
      const data = Object.values(this.resumo.recursosPorTipo)
      this.charts.push(new Chart(this.chartRecursosTipoRef.nativeElement, {
        type: 'bar',
        data: { labels, datasets: [{ data, backgroundColor: '#1976d2' }] },
        options: {
          responsive: true, maintainAspectRatio: true, indexAxis: 'y',
          plugins: { legend: { display: false }, title: { display: true, text: 'Por Tipo', font: { size: 11 } } },
          scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
        }
      }))
    }

    // Comissão - barra empilhada horizontal
    if (this.chartComissaoRef?.nativeElement) {
      const roles = ['Titulares', 'Suplentes', 'Presidente']
      const required = [4, 1, 1]
      const filled = [this.comissaoTitulares, this.comissaoSuplentes, this.comissaoPresidente]
      const remaining = required.map((r, i) => Math.max(0, r - filled[i]))
      this.charts.push(new Chart(this.chartComissaoRef.nativeElement, {
        type: 'bar',
        data: {
          labels: roles,
          datasets: [
            { label: 'Preenchidos', data: filled, backgroundColor: '#4caf50' },
            { label: 'Vagas', data: remaining, backgroundColor: '#e0e0e0' },
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false, indexAxis: 'y',
          scales: { x: { stacked: true, ticks: { precision: 0, stepSize: 1 } }, y: { stacked: true, ticks: { font: { size: 12 } } } },
          layout: { padding: { left: 10 } }, plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } }
        }
      }))
    }

    // SIGAD - doughnut gauge
    if (this.chartSigadRef?.nativeElement) {
      const enviados = this.sigadEnviados
      const pendentes = this.resumo.enviosSigad.length - enviados
      this.charts.push(new Chart(this.chartSigadRef.nativeElement, {
        type: 'doughnut',
        data: {
          labels: ['Enviados', 'Pendentes'],
          datasets: [{ data: [enviados, pendentes], backgroundColor: ['#4caf50', '#e0e0e0'] }]
        },
        options: {
          responsive: true, maintainAspectRatio: true,
          circumference: 180, rotation: -90,
          plugins: { legend: { display: false } }
        }
      }))
    }

    // Classificação por fase - bar chart (mesmo padrão dashboard)
    if (this.chartClassificacaoRef?.nativeElement) {
      const classif = this.resumo.classificacaoPorFase
      const fases = ['FASE_1', 'FASE_2']
      const aprovados = ['HABILITADO', 'CLASSIFICADO']
      this.charts.push(new Chart(this.chartClassificacaoRef.nativeElement, {
        type: 'bar',
        data: {
          labels: fases.map(f => this.formatFase(f)),
          datasets: [
            {
              label: 'Habilitados',
              data: fases.map(f => classif.filter(c => c.fase === f && aprovados.includes(c.classificacao)).reduce((s, c) => s + c.quantidade, 0)),
              backgroundColor: '#4caf50',
            },
            {
              label: 'Não habilitados',
              data: fases.map(f => classif.filter(c => c.fase === f && !aprovados.includes(c.classificacao)).reduce((s, c) => s + c.quantidade, 0)),
              backgroundColor: '#f44336',
            },
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          scales: { x: {}, y: { ticks: { precision: 0, stepSize: 1 } } },
          plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } }
        }
      }))
    }

    // Provas digitalizadas - gauge
    if (this.chartDigitalizacaoRef?.nativeElement) {
      const dig = this.resumo.provasDigitalizadas
      const pendentes = this.resumo.provasDigitalizadasTotal - dig
      this.charts.push(new Chart(this.chartDigitalizacaoRef.nativeElement, {
        type: 'doughnut',
        data: { labels: ['Digitalizadas', 'Pendentes'], datasets: [{ data: [dig, pendentes], backgroundColor: ['#4caf50', '#e0e0e0'] }] },
        options: { responsive: true, maintainAspectRatio: true, circumference: 180, rotation: -90, plugins: { legend: { display: false } } }
      }))
    }
  }

  getStatusColor(status: string): string {
    const map: { [k: string]: string } = {
      DEFERIDA: '#4caf50', DEFERIDA_SEM_PCD: '#66bb6a',
      INDEFERIDA: '#f44336', NAO_CONFIRMADA: '#ff9800'
    }
    return map[status] || '#9e9e9e'
  }


  private getParecerColor(parecer: string): string {
    const p = parecer.toUpperCase()
    if (p.includes('DEFERIDO') && !p.includes('INDEFERIDO')) return '#4caf50'
    if (p.includes('INDEFERIDO')) return '#f44336'
    return '#ff9800'
  }
}
